/**
 * Point Structure Models for Target Achievements
 *
 * Handles point structure templates with various types:
 * - Individual vs Cumulative slabs
 * - Flat, Fixed, Multiple point types
 * - Excel-based bulk configuration
 */

// ==================== ENUMS ====================

/**
 * Point Structure Type
 * 1 = Category (disabled in UI)
 * 2 = SKU
 */
export enum PointStructureType {
  CATEGORY = 1,
  SKU = 2,
}

/**
 * Slab Type
 * 1 = Individual
 * 2 = Cumulative
 */
export enum SlabType {
  INDIVIDUAL = 1,
  CUMULATIVE = 2,
}

/**
 * Point Type (for Individual slab)
 * 1 = Flat
 * 2 = Fixed
 * 3 = Multiple
 */
export enum PointType {
  FLAT = 1,
  FIXED = 2,
  MULTIPLE = 3,
}

/**
 * Cumulative Type
 * 1 = Collective
 * 2 = Singular
 */
export enum CumulativeType {
  COLLECTIVE = 1,
  SINGULAR = 2,
}

/**
 * Cumulative Sub Type
 * 1 = Flat
 * 2 = Fixed
 */
export enum CumulativeSubType {
  FLAT = 1,
  FIXED = 2,
}

// ==================== INTERFACES ====================

/**
 * Brand basic info for dropdown/multiselect
 */
export interface BrandBasicInfo {
  id: number;
  brand_name: string;
  brand_category_id?: number;
  sku?: string;
}

/**
 * Flat cumulative row (dynamic form array)
 */
export interface FlatCumulativeRow {
  points: number;
  min_threshold: number;
  max_threshold: number;
}

/**
 * Point structure template list item
 */
export interface PointStructureTemplate {
  id: number;
  template_id?: number; // Alias for id - used in some API responses
  template_name: string;
  type_points_structure: string; // Display name
  type_of_slab: string; // Display name
  point_type: string | null; // Display name
  type_of_cummulative: string | null; // Display name
  sub_type_of_cumulative: string | null; // Display name

  // IDs for reference
  type_of_pointstructure_id: number;
  type_of_slab_id: number;
  point_type_id: number | null;
  type_of_cumulative_id: number | null;
  typeof_cummulative_id?: number | null; // Alias for type_of_cumulative_id
  sub_type_of_cumulative_id: number | null;

  // Template details for view modal
  template_details: PointStructureTemplateDetail[];

  // For cumulative fixed type
  min_threshold?: number;
  max_threshold?: number;
}

/**
 * Template detail row (for view modal)
 */
export interface PointStructureTemplateDetail {
  id: number;

  // Brand info
  brand_id?: number;
  brand_ids?: number[] | string; // Can be array or comma-separated string
  brand_name?: string;
  brand_details?: string; // Comma-separated brand names for popover

  // Threshold values
  min_threshold?: number;
  max_threshold?: number;

  // Slab values (for Individual slab types)
  points_start_slab?: number;
  points_end_slab?: number;

  // Points/multiplier
  points_value?: number;
  points_multiplier?: number;
  total_points?: number;
}

/**
 * Create point structure request payload
 */
export interface CreatePointStructureRequest {
  name: string;
  type_of_pointstructure: number;
  type_of_slab: number;
  point_type?: number;
  type_of_cumulative?: number;
  sub_type_of_cumulative?: number;
  flat_cumulative?: FlatCumulativeRow[];
  flat_brand_id?: number[];
  fixed_min_threshold?: number;
  fixed_max_threshold?: number;
  excel_file?: File;
}

/**
 * Create point structure response
 */
export interface CreatePointStructureResponse {
  success: boolean;
  status: number;
  data: {
    status: string;
    approved: number;
    rejected: number;
    reason: RejectedReason[];
  };
}

/**
 * Rejected reason item
 */
export interface RejectedReason {
  [key: string]: string | number;
}

/**
 * Get point structures response
 */
export interface GetPointStructuresResponse {
  data: PointStructureTemplate[];
}

/**
 * Get brands response
 */
export interface GetBrandsResponse {
  data: BrandBasicInfo[];
}

// ==================== HELPER FUNCTIONS ====================

/**
 * Get display name for point structure type
 */
export function getPointStructureTypeLabel(type: number): string {
  switch (type) {
    case PointStructureType.CATEGORY:
      return 'Category';
    case PointStructureType.SKU:
      return 'SKU';
    default:
      return 'Unknown';
  }
}

/**
 * Get display name for slab type
 */
export function getSlabTypeLabel(type: number): string {
  switch (type) {
    case SlabType.INDIVIDUAL:
      return 'Individual';
    case SlabType.CUMULATIVE:
      return 'Cumulative';
    default:
      return 'Unknown';
  }
}

/**
 * Get display name for point type
 */
export function getPointTypeLabel(type: number | null): string {
  if (type === null) return '-';
  switch (type) {
    case PointType.FLAT:
      return 'Flat';
    case PointType.FIXED:
      return 'Fixed';
    case PointType.MULTIPLE:
      return 'Multiple';
    default:
      return '-';
  }
}

/**
 * Get display name for cumulative type
 */
export function getCumulativeTypeLabel(type: number | null): string {
  if (type === null) return '-';
  switch (type) {
    case CumulativeType.COLLECTIVE:
      return 'Collective';
    case CumulativeType.SINGULAR:
      return 'Singular';
    default:
      return '-';
  }
}

/**
 * Get display name for cumulative sub type
 */
export function getCumulativeSubTypeLabel(type: number | null): string {
  if (type === null) return '-';
  switch (type) {
    case CumulativeSubType.FLAT:
      return 'Flat';
    case CumulativeSubType.FIXED:
      return 'Fixed';
    default:
      return '-';
  }
}

/**
 * Get badge color class for slab type
 */
export function getSlabTypeBadgeClass(type: number): string {
  switch (type) {
    case SlabType.INDIVIDUAL:
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
    case SlabType.CUMULATIVE:
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
}

/**
 * Get badge color class for point type
 */
export function getPointTypeBadgeClass(type: number | null): string {
  if (type === null) return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  switch (type) {
    case PointType.FLAT:
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
    case PointType.FIXED:
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300';
    case PointType.MULTIPLE:
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
}
